import React, { useState } from 'react';
import { useSalaryComponents, useCreateSalaryComponent } from '@/api';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Badge, Modal } from '@/components/ui';
import { LoadingSpinner, Pagination } from '@/components/common';
import { Plus, DollarSign, Percent } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { extractPaginatedData } from '@/utils/pagination';

const typeConfig: Record<string, { label: string; color: string; icon: typeof DollarSign }> = {
  earning: { label: 'Начисление', color: 'bg-green-100 text-green-700', icon: DollarSign },
  deduction: { label: 'Вычет', color: 'bg-red-100 text-red-700', icon: Percent },
};

export default function SalaryComponentsPage() {
  const perPage = 9;
  const { can } = useAuth();
  const canManage = can('manage_payroll');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');

  const { data: componentsData, isLoading } = useSalaryComponents({ page, per_page: perPage, type: typeFilter });
  const createMutation = useCreateSalaryComponent();

  const { items: components, meta } = extractPaginatedData<any>(componentsData, page, perPage);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 page-shell">
      <div className="page-toolbar">
        <h1 className="text-3xl font-bold">Компоненты зарплаты</h1>
        {canManage && (
          <Button onClick={() => setShowForm(!showForm)}>
            <Plus className="w-4 h-4 mr-2" />
            Добавить компонент
          </Button>
        )}
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title="Новый компонент зарплаты"
      >
        <SalaryComponentForm
          onSubmit={async (data) => {
            await createMutation.mutateAsync(data);
            setShowForm(false);
          }}
          onCancel={() => setShowForm(false)}
          isLoading={createMutation.isPending}
        />
      </Modal>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <select
          value={typeFilter}
          onChange={(e) => {
            setPage(1);
            setTypeFilter(e.target.value);
          }}
          className="border rounded-md px-3 py-2"
        >
          <option value="">All types</option>
          <option value="earning">Начисления</option>
          <option value="deduction">Вычеты</option>
        </select>
      </div>

      {/* Components Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {components.length === 0 ? (
          <div className="col-span-full text-center py-10 text-gray-500">
            Компоненты зарплаты не настроены
          </div>
        ) : (
          components.map((component: any) => {
            const config = typeConfig[component.type] || typeConfig.earning;
            const Icon = config.icon;
            return (
              <Card key={component.id}>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${config.color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{component.name}</h3>
                        <p className="text-sm text-gray-500">{component.code}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
                      {config.label}
                    </span>
                  </div>
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Расчет:</span>
                      <span className="font-medium capitalize">{component.calc_method}</span>
                    </div>
                    {(component.default_amount || component.default_percent) && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Значение по умолч.:</span>
                        <span className="font-mono">
                          {component.calc_method === 'percent'
                            ? `${component.default_percent}%`
                            : Number(component.default_amount).toLocaleString()}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Налогооблаг.:</span>
                      <Badge variant={component.is_taxable ? 'default' : 'secondary'}>
                        {component.is_taxable ? 'Да' : 'Нет'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
      <Pagination meta={meta} onPageChange={setPage} />
    </div>
  );
}

interface SalaryComponentFormProps {
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

function SalaryComponentForm({ onSubmit, onCancel, isLoading }: SalaryComponentFormProps) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [type, setType] = useState('earning');
  const [calculationType, setCalculationType] = useState('fixed');
  const [defaultValue, setDefaultValue] = useState('');
  const [isTaxable, setIsTaxable] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      code: code.toUpperCase(),
      type,
      calc_method: calculationType,
      default_amount: calculationType === 'fixed' && defaultValue ? Number(defaultValue) : null,
      default_percent: calculationType === 'percent' && defaultValue ? Number(defaultValue) : null,
      is_taxable: isTaxable,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium mb-1">Название *</label>
          <Input
            placeholder="например, Транспортная надбавка"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Код *</label>
          <Input
            placeholder="например, TRANS_ALLOW"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Тип *</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            required
            className="w-full border rounded-md px-3 py-2"
          >
            <option value="earning">Начисление (+)</option>
            <option value="deduction">Вычет (-)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Тип расчета</label>
          <select
            value={calculationType}
            onChange={(e) => setCalculationType(e.target.value)}
            className="w-full border rounded-md px-3 py-2"
          >
            <option value="fixed">Фиксированная сумма</option>
            <option value="percent">Процент от базовой</option>
            <option value="formula">Формула</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            Значение по умолч. {calculationType === 'percent' ? '(%)' : ''}
          </label>
          <Input
            type="number"
            placeholder={calculationType === 'percentage' ? 'например, 10' : 'например, 500000'}
            value={defaultValue}
            onChange={(e) => setDefaultValue(e.target.value)}
          />
        </div>
        <div className="flex items-center">
          <label className="flex items-center gap-2 mt-6">
            <input
              type="checkbox"
              checked={isTaxable}
              onChange={(e) => setIsTaxable(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Налогооблагаемый</span>
          </label>
        </div>
      </div>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>
          Отмена
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Создание...' : 'Создать компонент'}
        </Button>
      </div>
    </form>
  );
}


